import { css } from "@emotion/css";
import { Selection, SelectionMode, Text } from "@fluentui/react";
import { OrderedMap } from "immutable";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  DashboardFilter,
  DashboardGrid,
  DashboardListActionsRef,
  FilterSchema,
  FilterState,
  IDashboardGridProps,
  IDashboardGridRef,
  IDashboardListActionsState,
  GLDashboardRow,
  JemConfiguration,
  LoadingStatus,
  DomainDataObjects,
  DomainDataEnum,
  GeneralLedgerActionResult,
  IIndexedTile
} from "@jem/components";

import { glFilterRows } from "../../../GeneralLedger/components/GLDashboard/GLDashboard.filterRows";
import { GLTileNames } from "../../../GeneralLedger/components/GLDashboard/GLDashboard.Requests";
import { AsyncSubject } from "rxjs";

// eslint-disable-next-line
const isEqual = require("lodash/isEqual");

export interface AttachmentDialogRef {
  open: (item: GLDashboardRow) => void;
  getOnSubmitAsyncSubject: () => AsyncSubject<boolean | null>;
}

export interface UserDashboardProps {
  userAlias: string;
  configuration: JemConfiguration["GeneralLedgerApi"];
  items: GLDashboardRow[];
  tilesAreLoading: LoadingStatus;
  dataIsLoading: LoadingStatus;
  uniqueIdForDashboardsColumnGenerator: IDashboardGridProps["idForLocalStorage"];
  columnGenerator: () => IDashboardGridProps["columnGenerator"];
  tiles: OrderedMap<string, IIndexedTile>;
  theIndexFromTheColumnsThatIsInitallySortedWith?: number;
  currentTile?: GLTileNames;
  onTileChange?: (tile: GLTileNames) => void;
  buttonCalculator: (rows: GLDashboardRow[]) => IDashboardListActionsState;
  onAction?: (action: GeneralLedgerActionResult, currentTile: string | null) => void;
  domainData: Pick<DomainDataObjects, DomainDataEnum.StatusActions>;
}

const dashboardFilters: FilterSchema<GLDashboardRow>[] = [
  {
    filterName: "Co Code",
    columnName: "companyCode"
  },
  {
    filterName: "FY",
    columnName: "fiscalYear"
  },
  {
    filterName: "FP",
    columnName: "fiscalPeriod"
  },
  {
    filterName: "Format",
    columnName: "format"
  },
  {
    filterName: "Due Date",
    columnName: "dueDate"
  },
  {
    filterName: "JE Status",
    columnName: "status"
  },
  {
    filterName: "Poster",
    columnName: "poster"
  },
  {
    filterName: "Reviewer",
    columnName: "reviewer"
  },
  {
    filterName: "Backup Posters",
    columnName: "additionalPosters"
  },
  {
    filterName: "Ops Detail",
    columnName: "opsDetailsName"
  },
  {
    filterName: "Checklist Name",
    columnName: "jeChecklistName"
  },
  {
    filterName: "Task Type",
    columnName: "isAdhocOrStd"
  } 
];

const UserDashboard = (props: UserDashboardProps): ReturnType<React.FC<UserDashboardProps>> => {
  const location = useLocation();
  const dashboardGridRef = useRef<IDashboardGridRef>(null);
  const dashboardActionsRef = useRef<DashboardListActionsRef>(null);
  // const attachmentRef = useRef<AttachmentDialogRef>(null);

  const [filteredItems, setFilteredItems] = useState<GLDashboardRow[]>([]);
  const [filterState, setFilterState] = useState<FilterState | null>(null);

  useEffect(() => {
    if (filterState) {
      location.state = {
        filterState
      };
    }
  }, [filterState]);

  const filterItemsRebuildTiles = (newFilterState?: FilterState) => {
    if (newFilterState) {
      if (newFilterState.currentTile !== props.currentTile && newFilterState.currentTile && props.onTileChange) {
        props.onTileChange(newFilterState.currentTile as GLTileNames);
      }
      if (!isEqual(newFilterState, filterState)) {
        setFilterState(newFilterState);
      }
    }
  };

  useEffect(
    function onNewGridItemsReloadFilteredItems() {
      if (filterState) {
        const newItems = glFilterRows(filterState, dashboardFilters, props.items);
        setFilteredItems(newItems);
      } else {
        setFilteredItems(props.items);
      }
    },
    [props.items, filterState]
  );

  return (
    <>
      <div
        className={css`
          position: relative;
          flex-grow: 1;
          width: 100%;
        `}
      >
        <DashboardFilter<GLDashboardRow>
          filters={dashboardFilters}
          singleSelectionFilters={[]}
          searchEnabledFilters={["Co Code"]}
          onFilterChanged={filterItemsRebuildTiles}
          loadingStatus={props.tilesAreLoading}
          tilesAndFilters={{
            gridItems: props.items,
            tiles: props.tiles,
            currentTile: props.currentTile || ""
          }}
          location={location}
        />
      </div>

      <div
        className={css`
          display: inline-flex;
          gap: 16px;
          flex-direction: column;
          flex-wrap: wrap;
          justify-content: center;
          align-items: stretch;
          width: 100%;
          margin: 16px 0 16px 0;
        `}
      >
        {props.dataIsLoading !== LoadingStatus.Pending && props.items.length == 0 ? (
          <Text
            variant="xLarge"
            style={{
              color: "var(--accent-font-color, black)"
            }}
          >
            No Items to show.
          </Text>
        ) : (
          <>
            <DashboardGrid
              idForLocalStorage={props.uniqueIdForDashboardsColumnGenerator}
              columnGenerator={props.columnGenerator()}
              isSortedIndex={props.theIndexFromTheColumnsThatIsInitallySortedWith}
              items={filteredItems}
              isSortedDescending={true}
              customRef={dashboardGridRef}
              selectionMode={SelectionMode.multiple}
              height={`460px`}
              loadingStatus={props.dataIsLoading}
              onSelectionChanged={(selection: Selection) => {
                if (dashboardActionsRef.current) {
                  const userSelectedItems = selection.getSelection();
                  dashboardActionsRef.current.refreshSelection(userSelectedItems as GLDashboardRow[]);
                }
              }}
            ></DashboardGrid>
          </>
        )}
      </div>
    </>
  );
};

UserDashboard.displayName = "UserDashboard";
export default UserDashboard;
